import React, { FC, useEffect, useState } from 'react'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Alert from 'react-bootstrap/Alert'
import FhirLoader from '../../FhirLoader'
import Modal from 'react-bootstrap/Modal'
import Card from 'react-bootstrap/Card'
import { useFormik } from 'formik'
import { ForgotPasswordSchema, ForgotUsernameSchema } from '../../../utils/validation'
import { addCompanyPhoneMessage } from '../../../utils/helpers'
import Turnstile from 'react-turnstile'
import useEnvironment from '../../../hooks/location/useEnviroment'

type ForgotLoginModalProps = {
  show: boolean;
  onHide: () => void;
  modalType: string;
  forgotLogin: any;
}

type FormValues = {
  memberId?: string;
  email?: string;
  username?: string;
  captcha: string | null;
}

const ForgotLoginModal: FC<ForgotLoginModalProps> = ({ show, onHide, modalType, forgotLogin }) => {
  const [alertState, setAlertState] = useState({
    show: false,
    variant: 'success',
    message: ''
  })
  const { data: environmentData, isSuccess: isEnvironmentSuccess } = useEnvironment()

  const formik = useFormik<FormValues>({
    initialValues: {
      ...(modalType === 'Username' ? { memberId: '', email: '' } : { username: '' }),
      captcha: null
    },
    enableReinitialize: true,
    validationSchema: modalType === 'Username' ? ForgotUsernameSchema : ForgotPasswordSchema,
    onSubmit: (values) => {
      forgotLogin.mutate({
        ...values,
        type: modalType,
      })
    }
  })

  useEffect(() => {
    if (forgotLogin.isSuccess) {
      setAlertState({
        show: true,
        variant: 'success',
        message:
          modalType === 'Username'
            ? 'If an account matching the information provided exists, an email has been sent.'
            : 'Please check your email for next steps.'
      })
    }

    if (forgotLogin.isError) {
      setAlertState({
        show: true,
        variant: 'danger',
        message: addCompanyPhoneMessage(forgotLogin.error.response.text)
      })
    }
  }, [
    forgotLogin.isSuccess,
    forgotLogin.isError,
    forgotLogin.data,
    modalType
  ])

  const handleCloseModal = () => {
    setAlertState({
      show: false,
      variant: 'success',
      message: ''
    })

    onHide()
  }
  return (
    <Modal show={show} onHide={handleCloseModal} size='lg' aria-labelledby="forgot-login-modal">
      <Modal.Header closeButton>
        <Modal.Title id="forgot-login-modal-title" className='font-primary'>Forgot {modalType}?</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form noValidate onSubmit={formik.handleSubmit}>
          {
            alertState.show && (
              <Alert variant={alertState.variant} dismissible onClose={() => setAlertState((ps => ({ ...ps, show: false })))}>
                {alertState.message}
              </Alert>
            )
          }
          <Card.Text>Please enter your {modalType === 'Username' ? 'Member ID and the email address' : 'username'} associated with your account.</Card.Text>
          {
            modalType === 'Username' && (
              <>
                <Form.Group>
                  <Form.Label htmlFor='memberId'>Member ID</Form.Label>
                  <Form.Control
                    id='memberId'
                    type='text'
                    name='memberId'
                    value={formik.values.memberId}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={!!formik.errors.memberId && !!formik.touched.memberId}
                    aria-invalid={!!formik.errors.memberId && !!formik.touched.memberId}
                    aria-describedby={
                      formik.errors.memberId && formik.touched.memberId ? 'memberId-error' : ''
                    }
                    aria-required="true"
                  />
                  <Form.Control.Feedback id="memberId-error" type="invalid" role="alert">
                    {formik.errors.memberId}
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group>
                  <Form.Label htmlFor='email'>Email Address</Form.Label>
                  <Form.Control
                    id='email'
                    type='text'
                    name='email'
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={!!formik.errors.email && !!formik.touched.email}
                    aria-invalid={!!formik.errors.email && !!formik.touched.email}
                    aria-describedby={formik.errors.email && formik.touched.email ? 'email-error' : ''}
                    aria-required="true"
                  />
                  <Form.Control.Feedback id="email-error" type="invalid" role="alert">
                    {formik.errors.email}
                  </Form.Control.Feedback>
                </Form.Group>
              </>
            )
          }
          {
            modalType === 'Password' && (
              <>
                <Form.Group>
                  <Form.Label htmlFor='username'>Username</Form.Label>
                  <Form.Control
                    id='username'
                    type='text'
                    name='username'
                    value={formik.values.username}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={!!formik.errors.username && !!formik.touched.username}
                    aria-invalid={!!formik.errors.username && !!formik.touched.username}
                    aria-describedby={formik.errors.username && formik.touched.username ? 'username-error' : ''}
                    aria-required="true"
                  />
                  <Form.Control.Feedback id="username-error" type="invalid" role="alert">
                    {formik.errors.username}
                  </Form.Control.Feedback>
                </Form.Group>
              </>
            )
          }
          <div className='my-3'>
            {
              isEnvironmentSuccess && (
                <Turnstile
                  sitekey={environmentData?.turnstileSiteKey || ''}
                  onSuccess={(value) => formik.setFieldValue('captcha', value)}
                  theme='light'
                  execution="render"
                  aria-label="CAPTCHA: Please verify that you are not a robot"
                />
              )
            }
            {
              Boolean(!!formik.errors.captcha && formik.touched.captcha) && (
                <Form.Text className='text-danger'>
                  {formik.errors.captcha}
                </Form.Text>
              )
            }
          </div>
          <div className='float-right mt-3'>
            <Button className='me-2' variant='light' type='button' onClick={handleCloseModal}>
              Close
            </Button>
            <Button
              variant='primary'
              type='submit'
            >
              {forgotLogin.isLoading ? (
                <FhirLoader small />
              ) : (
                'Submit'
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  )
}

export default ForgotLoginModal
